// Origin-parity endpoints (browserdav-nephele-parity Phase 5).
//
// A BrowserDAV origin will never host the domain's /auth registry — users,
// invites, handles, the chain store, and revocation lists live on the node
// server. But a peer origin should answer the endpoints agents actually
// probe: /status (health) and /auth/verify (token verification, which
// BrowserDAV already performs client-side on every request anyway).
//
// Token creation deliberately has NO unattended endpoint here (decision,
// Phase 5): minting requires the owner's private key, and on a browser
// origin that key stays behind the interactive grant flow
// (accessGrants.js — request → owner prompt → mint → deliver). Exposing a
// POST /auth/create-token equivalent would let any peer with transport
// reach mint tokens without the owner seeing it. Use the UI, the grant
// flow, or the node server's API.
//
// Pure response builders live here so the conformance suite can check
// shape parity against the node server without a browser.

export const BROWSERDAV_VERSION = '1.0.0'

// /auth/* routes a BrowserDAV origin will NEVER serve — returned in the
// 404 explainer so clients can feature-detect instead of guessing.
// The authority for all of these is the domain's node server.
export const NEVER_SERVED_AUTH_ROUTES = [
    '/auth/{domain}/users',
    '/auth/{domain}/handles',
    '/auth/{domain}/invites',
    '/auth/{domain}/chains',
    '/auth/{domain}/revocations',
    '/auth/{domain}/stored-tokens',
    '/auth/create-token',
    '/auth/register-user',
    '/auth/device-links',
]

/**
 * /status — same shape as the node server's health endpoint:
 * { status, timestamp, name, version, uptime }.
 */
export function buildStatusResponse({ name = 'BrowserDAV', version = BROWSERDAV_VERSION, startTime }) {
    return {
        status: 200,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({
            status: 'ok',
            timestamp: new Date().toISOString(),
            name,
            version,
            uptime: Math.floor((Date.now() - startTime) / 1000),
        }),
    }
}

/**
 * /auth/verify — mirrors the GeoCamera http-token-grant shape:
 * always 200 with { valid, reason?, ... } (verification outcomes are data,
 * not transport errors); 400 only when no token was supplied.
 */
export function buildVerifyResponse(result) {
    return {
        status: 200,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(result),
    }
}

export function buildVerifyBadRequest() {
    return {
        status: 400,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ error: 'Provide token or tokenId query parameter' }),
    }
}

/**
 * 404 explainer for /auth/* routes this origin never serves — clients
 * feature-detect from `neverServed` + `authority` instead of guessing.
 */
export function buildAuthNotServedResponse(path) {
    return {
        status: 404,
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({
            error: 'not_served',
            message: `A BrowserDAV origin does not serve ${path}. The domain's auth registry (users, invites, chains, revocations, token creation) lives on the node server.`,
            served: ['/status', '/auth/verify'],
            neverServed: NEVER_SERVED_AUTH_ROUTES,
            authority: 'node-server',
        }),
    }
}
